import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Modal,
  Typography,
  IconButton,
  Grid,
  MenuItem,
  Paper,
  Divider,
  Card,
  CardContent,
  Stack,
  Chip,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Close,
  AttachMoney,
  CalendarToday,
  Settings,
  ExpandMore,
  Save,
  Clear,
  CheckCircle,
} from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { BackdropComponent } from "../../../shared-components/backdrop/Backdrop";
import { createDebits } from "../../../services/request/Debits";
import { useTheme } from "@mui/material/styles";
import AmountInput from "../../../shared-components/amount-input/AmountInput";

const durationTypes = ["Days", "Weeks", "Months", "Years"];
const statuses = ["PENDING", "ACTIVE", "CLOSED", "DEFAULTED"];
const Type = ["INTEREST", "SHORT_TERM_DEBIT"];

interface DebitCaptureModalProps {
  open: boolean;
  borrowerId: string;
  handleDebitModelClose: () => void;
  onDebitCreated?: () => void;
}

const DebitCaptureModal: React.FC<DebitCaptureModalProps> = ({
  open,
  borrowerId,
  handleDebitModelClose,
  onDebitCreated,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loader, setLoader] = React.useState(false);
  const { leadId = "" } = useParams();
  const [formData, setFormData] = useState({
    amount: '',
    type: "",
    outstanding: '',
    interestRate: '',
    durationType: "",
    startDate: "",
    endDate: "",
    emiAmount: '',
    paymentDate: "",
    status: "",
  });

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleClear = () => {
    setFormData({
      amount: '',
      type: "",
      outstanding: '',
      interestRate: '',
      durationType: "",
      startDate: "",
      endDate: "",
      emiAmount: '',
      paymentDate: "",
      status: "",
    });
  };

  const handleSubmit = async () => {
    setLoader(true);
    try {
      const payload = {
        ...formData,
      };
      createDebits(borrowerId, payload)
        .then((result) => {
          console.log("Result ", result);
          setLoader(false);
          if (result.status === 200 && result.data.loanId) {
            toast.success("Outstanding Amount Added Successfully!");
            // Reload the debits list
            if (onDebitCreated) {
              onDebitCreated();
            }
            handleDebitModelClose();
          } else if (result.status === 403) {
            toast.error("Invalid Login - Please check your credentials", { 
              position: "bottom-center",
              autoClose: 5000
            });
          } else {
            toast.error(`Failed to create outstanding: ${result.statusText || 'Unknown error'}`, {
              position: "bottom-center",
              autoClose: 5000
            });
          }
        })
        .catch((err) => {
          console.log("API Error:", err);
          setLoader(false);
          
          // Handle different types of errors
          if (err.response) {
            const { status, data } = err.response;
            
            switch (status) {
              case 400:
                // Bad Request - Parse the specific error message
                let errorMessage = "Invalid data provided";
                if (data && data.message) {
                  errorMessage = data.message;
                } else if (data && data.error) {
                  errorMessage = data.error;
                }
                
                toast.error(`❌ ${errorMessage}`, {
                  position: "top-right",
                  autoClose: 7000
                });
                break;
                
              case 401:
                toast.error("🔐 Unauthorized - Please login again", {
                  position: "top-right",
                  autoClose: 5000
                });
                break;
                
              case 403:
                toast.error("🚫 Access Denied - Insufficient permissions", {
                  position: "top-right",
                  autoClose: 5000
                });
                break;
                
              case 404:
                toast.error("🔍 Customer not found", {
                  position: "top-right",
                  autoClose: 5000
                });
                break;
                
              case 422:
                toast.error("📝 Validation failed - Please check your inputs", {
                  position: "top-right",
                  autoClose: 5000
                });
                break;
                
              case 500:
                toast.error("🔧 Server error - Please try again later", {
                  position: "top-right",
                  autoClose: 5000
                });
                break;
                
              default:
                toast.error(`⚠️ Failed to create outstanding (Error ${status})`, {
                  position: "top-right",
                  autoClose: 5000
                });
            }
          } else if (err.request) {
            // Network error
            toast.error("🌐 Network error - Please check your connection", {
              position: "top-right",
              autoClose: 5000
            });
          } else {
            // Other errors
            toast.error("❌ An unexpected error occurred", {
              position: "top-right",
              autoClose: 5000
            });
          }
        });
    } catch (err) {
      console.log("Unexpected Error:", err);
      setLoader(false);
      toast.error("❌ An unexpected error occurred while processing your request", {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  // Field configurations for better organization
  const amountFields = [
    { name: "amount", label: "Principal Amount" },
    { name: "outstanding", label: "Outstanding Amount" },
    { name: "interestRate", label: "Interest Rate (%)" },
    { name: "emiAmount", label: "EMI Amount" },
  ];

  const dateFields = [
    { name: "startDate", label: "Start Date" },
    { name: "endDate", label: "End Date" },
    { name: "paymentDate", label: "Payment Date" },
  ];

  const modalStyle = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isMobile ? "95vw" : "600px",
    maxWidth: "95vw",
    maxHeight: "90vh",
    bgcolor: "background.paper",
    borderRadius: 3,
    boxShadow: 24,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  };

  const FormContent = React.useMemo(() => (
    <Box sx={{ flexGrow: 1, overflow: "auto", p: 0 }}>
      <Stack spacing={2}>
        {/* Amount Section */}
        <Accordion defaultExpanded>
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              '& .MuiAccordionSummary-content': { alignItems: 'center' }
            }}
          >
            <AttachMoney sx={{ mr: 1 }} />
            <Typography variant="h6" fontWeight="bold">
              Amount details
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            <Grid container spacing={2}>
              {amountFields.map((field) => {
                const isAmountField = ['amount', 'outstanding', 'emiAmount'].includes(field.name);
                const InputComponent = isAmountField ? AmountInput : TextField;
                
                return (
                  <Grid item xs={12} sm={6} key={field.name}>
                    <InputComponent
                      label={field.label}
                      name={field.name}
                      fullWidth
                      value={formData[field.name as keyof typeof formData] || ''}
                      onChange={handleChange}
                      variant="outlined"
                      size="small"
                      InputProps={{
                        inputProps: { min: 0, step: field.name === "interestRate" ? 0.01 : 1 }
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Date Section */}
        <Accordion defaultExpanded>
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ 
              bgcolor: 'secondary.main', 
              color: 'white',
              '& .MuiAccordionSummary-content': { alignItems: 'center' }
            }}
          >
            <CalendarToday sx={{ mr: 1 }} />
            <Typography variant="h6" fontWeight="bold">
              Date information
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            <Grid container spacing={2}>
              {dateFields.map((field) => (
                <Grid item xs={12} sm={4} key={field.name}>
                  <TextField
                    label={field.label}
                    name={field.name}
                    type="date"
                    fullWidth
                    value={formData[field.name as keyof typeof formData] || ''}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Configuration Section */}
        <Accordion defaultExpanded>
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ 
              bgcolor: 'success.main', 
              color: 'white',
              '& .MuiAccordionSummary-content': { alignItems: 'center' }
            }}
          >
            <Settings sx={{ mr: 1 }} />
            <Typography variant="h6" fontWeight="bold">
              Configuration
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="Debit type"
                  name="type"
                  fullWidth
                  value={formData.type || ''}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                >
                  {Type.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option.replace(/_/g, ' ')}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="Duration type"
                  name="durationType"
                  fullWidth
                  value={formData.durationType || ''}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                >
                  {durationTypes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="Status"
                  name="status"
                  fullWidth
                  value={formData.status || ''}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                >
                  {statuses.map((option) => (
                    <MenuItem key={option} value={option}>
                      <Chip 
                        label={option}
                        size="small"
                        color={
                          option === 'Active' ? 'success' :
                          option === 'Pending' ? 'warning' :
                          option === 'Closed' ? 'info' : 'error'
                        }
                        sx={{ mr: 1 }}
                      />
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Interest Projection - Only show when type is INTEREST */}
        {formData.type === "INTEREST" && formData.amount && formData.interestRate && formData.startDate && formData.endDate && (
          <Card elevation={4} sx={{ bgcolor: 'info.light', border: '2px solid', borderColor: 'info.main' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" color="info.dark" gutterBottom fontWeight="bold">
                Interest projection
              </Typography>
              {(() => {
                const principal = Number(formData.amount || 0);
                const rate = Number(formData.interestRate || 0);
                const startDate = new Date(formData.startDate);
                const endDate = new Date(formData.endDate);
                const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
                const monthsDiff = Math.ceil(daysDiff / 30);
                const yearsDiff = daysDiff / 365;

                // Calculate monthly interest: (Principal / 100) × Rate
                const monthlyInterest = (principal / 100) * rate;

                // Calculate total interest based on duration type
                let calculatedInterest = 0;
                let periodLabel = '';
                let numberOfMonths = 0;

                if (formData.durationType === "Days") {
                  numberOfMonths = daysDiff / 30;
                  calculatedInterest = monthlyInterest * numberOfMonths;
                  periodLabel = `${daysDiff} days (≈ ${numberOfMonths.toFixed(1)} months)`;
                } else if (formData.durationType === "Weeks") {
                  const weeks = Math.ceil(daysDiff / 7);
                  numberOfMonths = weeks / 4.33; // Average weeks per month
                  calculatedInterest = monthlyInterest * numberOfMonths;
                  periodLabel = `${weeks} weeks (≈ ${numberOfMonths.toFixed(1)} months)`;
                } else if (formData.durationType === "Months") {
                  numberOfMonths = monthsDiff;
                  calculatedInterest = monthlyInterest * numberOfMonths;
                  periodLabel = `${monthsDiff} months`;
                } else if (formData.durationType === "Years") {
                  numberOfMonths = yearsDiff * 12;
                  calculatedInterest = monthlyInterest * numberOfMonths;
                  periodLabel = `${yearsDiff.toFixed(2)} years (${Math.round(numberOfMonths)} months)`;
                }

                const totalAmount = principal + calculatedInterest;

                return (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Period: <strong>{periodLabel}</strong>
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Principal amount: <strong>₹{principal.toLocaleString()}</strong>
                        </Typography>
                        <Typography variant="body2" color="success.main" gutterBottom fontWeight="bold">
                          Interest rate: <strong>{rate}% per month</strong>
                        </Typography>
                        <Typography variant="h6" color="warning.dark" gutterBottom fontWeight="bold" sx={{ mt: 1 }}>
                          Monthly interest: ₹{monthlyInterest.toFixed(2).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Formula: ({principal.toLocaleString()} ÷ 100) × {rate} = ₹{monthlyInterest.toFixed(2)}
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Number of months: <strong>{numberOfMonths.toFixed(1)}</strong>
                        </Typography>
                        <Typography variant="h6" color="info.dark" gutterBottom fontWeight="bold">
                          Total interest ({periodLabel}): ₹{calculatedInterest.toFixed(2).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Calculation: ₹{monthlyInterest.toFixed(2)} × {numberOfMonths.toFixed(1)} months = ₹{calculatedInterest.toFixed(2)}
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="h5" color="error.main" fontWeight="bold" sx={{ mt: 1 }}>
                          Total amount: ₹{totalAmount.toFixed(2).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Principal + Total Interest = ₹{principal.toLocaleString()} + ₹{calculatedInterest.toFixed(2)} = ₹{totalAmount.toFixed(2)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Summary Card */}
        <Card elevation={3} sx={{ bgcolor: 'grey.50', border: '2px solid', borderColor: 'primary.main' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom fontWeight="bold">
              Summary
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Principal: <strong>₹{Number(formData.amount || 0).toLocaleString()}</strong>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Outstanding: <strong>₹{Number(formData.outstanding || 0).toLocaleString()}</strong>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Interest: <strong>{formData.interestRate || '0'}%</strong>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  EMI: <strong>₹{Number(formData.emiAmount || 0).toLocaleString()}</strong>
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  ), [formData, handleChange, loader]);

  return (
    <>
      {isMobile ? (
        <Dialog
          fullScreen
          open={open}
          onClose={handleDebitModelClose}
        >
          <DialogTitle sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2
          }}>
            <Box display="flex" alignItems="center">
              <AttachMoney sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">
                Add Outstanding Amount
              </Typography>
            </Box>
            <IconButton color="inherit" onClick={handleDebitModelClose}>
              <Close />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ p: 1 }}>
            <Box sx={{ p: 1 }}>
              {FormContent}
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ p: 2, gap: 1, bgcolor: 'grey.50' }}>
            <Button 
              variant="outlined" 
              color="warning" 
              onClick={handleClear}
              startIcon={<Clear />}
              fullWidth
            >
              Clear
            </Button>
            <Button 
              variant="contained" 
              color="success" 
              onClick={handleSubmit}
              startIcon={<CheckCircle />}
              fullWidth
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <Modal open={open} onClose={handleDebitModelClose}>
          <Box sx={modalStyle}>
            <BackdropComponent visible={loader} />
            <ToastContainer />
            
            {/* Header */}
            <Box sx={{ 
              p: 2, 
              bgcolor: 'primary.main', 
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Box display="flex" alignItems="center">
                <AttachMoney sx={{ mr: 1, fontSize: '1.5rem' }} />
                <Typography variant="h5" fontWeight="bold">
                  Add Outstanding Amount
                </Typography>
              </Box>
              <IconButton color="inherit" onClick={handleDebitModelClose}>
                <Close />
              </IconButton>
            </Box>
            
            {/* Content */}
            <Box sx={{ p: 2, flexGrow: 1, overflow: 'auto' }}>
              {FormContent}
            </Box>
            
            {/* Footer */}
            <Box sx={{ 
              p: 2, 
              bgcolor: 'grey.50',
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              gap: 2,
              justifyContent: 'space-between'
            }}>
              <Button 
                variant="outlined" 
                color="warning" 
                onClick={handleClear}
                startIcon={<Clear />}
                sx={{ minWidth: 120 }}
              >
                Clear Form
              </Button>
              <Button 
                variant="contained" 
                color="success" 
                onClick={handleSubmit}
                startIcon={<CheckCircle />}
                sx={{ minWidth: 120 }}
              >
                Submit
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default DebitCaptureModal;
